import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Select, Progress } from 'antd';
import { SendOutlined, PaperClipOutlined, CloseOutlined, FilePdfOutlined, FolderOpenOutlined } from '@ant-design/icons';
import Avatar from './Avatar';
import { PRIORITES } from '../../services/conversationService';
import { STATUT_MAP } from '../../variables/constants';
import { chercherDossiers } from './liensSinistre';
import i18n from '../../i18n';
import {
  MIMES_ACCEPTES, TAILLE_MAX, T, astuceEntreeVue, compresserImage, marquerAstuceEntreeVue, marqueurDossier,
  marqueurMention, normaliser, tailleLisible, texteSansMarqueurs,
} from './utils';

/**
 * Textarea auto-extensible (1 à 6 lignes), priorité, pièces jointes, mentions.
 *
 * Entrée envoie, Maj+Entrée retourne à la ligne. La priorité revient à NORMAL
 * après chaque envoi : un « urgent » qui reste coché envoie le suivant en urgent
 * par accident, et l'urgent cesse alors de vouloir dire quelque chose.
 *
 * Les images sont réduites dans le navigateur AVANT de partir (voir
 * `compresserImage`), puis téléversées immédiatement : à l'envoi du message,
 * seules les clés sont jointes. Un fichier refusé (type, taille) ne bloque pas
 * les autres.
 *
 * MENTIONS. Un `@` en début de mot ouvre la liste des membres de la conversation
 * - d'eux seuls : on ne peut pas nommer quelqu'un qui n'est pas dans l'échange,
 * et le serveur ramène en texte plat toute puce vers un non-membre. Le marqueur
 * inséré porte l'UUID ; le nom n'est qu'un affichage, réécrit côté serveur. Tant
 * que le menu est ouvert, Entrée choisit au lieu d'envoyer.
 *
 * DOSSIERS. Un `#` en début de mot - ou le bouton « Citer un dossier » - cherche
 * parmi les dossiers sinistres (numéro, assuré, immatriculation). Le marqueur inséré
 * porte l'identifiant du dossier : la puce l'ouvrira directement, sans passer par
 * une recherche au clic qui pouvait mener à la liste plutôt qu'au dossier.
 */

/** Le jeton s'arrête au premier espace ; le `@` doit ouvrir un mot (pas `nom@domaine`). */
const MOTIF_JETON = /(?:^|\s)@([\p{L}\p{N}_'’.-]*)$/u;
/** Même règle pour `#`, en admettant la barre d'un numéro de dossier (2026/0363). */
const MOTIF_DOSSIER = /(?:^|\s)#([\p{L}\p{N}_'’./-]*)$/u;
const MAX_PROPOSITIONS = 8;
const MIN_RECHERCHE_DOSSIER = 2;

const ZoneSaisie = forwardRef(({
  conversationId, brouillon, surBrouillon, surEnvoyer, surSaisie, reponseA, surAnnulerReponse, televerser, desactive,
  membres = [], moiTrackingId,
}, ref) => {
  const { t } = useTranslation('conversations');
  const [priorite, setPriorite] = useState('NORMAL');
  const [fichiers, setFichiers] = useState([]); // { cle, nom, taille, image, apercu, progression, piece, erreur }
  const [astuce, setAstuce] = useState(() => !astuceEntreeVue());
  // null | { type: 'mention' | 'dossier', debut, jeton } - position du `@`/`#` et texte tapé après
  const [menu, setMenu] = useState(null);
  const [index, setIndex] = useState(0);
  const [dossiers, setDossiers] = useState({ jeton: null, liste: [], chargement: false });
  const zone = useRef(null);
  const entree = useRef(null);
  const derniereSaisie = useRef(0);
  const arretSaisie = useRef(null);

  // Hauteur auto : 1 à 6 lignes, puis défilement interne.
  useEffect(() => {
    const el = zone.current;
    if (!el) return;
    el.style.height = 'auto';
    const ligne = 15 * 1.5;
    el.style.height = `${Math.min(el.scrollHeight, ligne * 6 + 16)}px`;
  }, [brouillon]);

  useEffect(() => { zone.current?.focus(); setMenu(null); }, [conversationId]);
  useEffect(() => { if (reponseA) zone.current?.focus(); }, [reponseA]);

  // Membres mentionnables : actifs, sans moi, filtrés sans accents ni casse sur
  // n'importe quel mot du nom (« mensa » trouve « Kodjo Mensah »).
  const propositions = useMemo(() => {
    if (!menu || menu.type !== 'mention') return [];
    const q = normaliser(menu.jeton);
    return membres
      .filter((m) => m.actif !== false && m.utilisateurTrackingId !== moiTrackingId)
      .filter((m) => !q || normaliser(m.nomComplet).split(/\s+/).some((mot) => mot.startsWith(q)) || normaliser(m.nomComplet).includes(q))
      .slice(0, MAX_PROPOSITIONS);
  }, [menu, membres, moiTrackingId]);

  useEffect(() => { setIndex(0); }, [menu?.jeton, menu?.type]);

  // Recherche de dossiers : temporisée, et une réponse arrivée après une frappe
  // plus récente est ignorée - sinon « 03 » pourrait écraser les résultats de « 0363 ».
  const jetonDossier = menu?.type === 'dossier' ? menu.jeton : null;
  useEffect(() => {
    if (jetonDossier === null) { setDossiers({ jeton: null, liste: [], chargement: false }); return undefined; }
    if (jetonDossier.length < MIN_RECHERCHE_DOSSIER) { setDossiers({ jeton: jetonDossier, liste: [], chargement: false }); return undefined; }
    let actif = true;
    setDossiers((d) => ({ ...d, chargement: true }));
    const t = setTimeout(async () => {
      const liste = await chercherDossiers(jetonDossier, MAX_PROPOSITIONS);
      if (actif) setDossiers({ jeton: jetonDossier, liste, chargement: false });
    }, 250);
    return () => { actif = false; clearTimeout(t); };
  }, [jetonDossier]);

  const options = menu?.type === 'dossier' ? dossiers.liste : propositions;
  /**
   * Le menu suit le curseur, pas la fin du texte : on corrige souvent une phrase
   * au milieu d'un message déjà écrit.
   */
  const analyser = useCallback((texte, curseur) => {
    const avant = texte.slice(0, curseur);
    const mention = MOTIF_JETON.exec(avant);
    if (mention) { setMenu({ type: 'mention', debut: curseur - mention[1].length - 1, jeton: mention[1] }); return; }
    const dossier = MOTIF_DOSSIER.exec(avant);
    if (dossier) { setMenu({ type: 'dossier', debut: curseur - dossier[1].length - 1, jeton: dossier[1] }); return; }
    setMenu(null);
  }, []);

  const choisir = (element) => {
    if (!menu || !element) return;
    const el = zone.current;
    const curseur = el?.selectionStart ?? brouillon.length;
    const marqueur = menu.type === 'dossier' ? marqueurDossier(element) : marqueurMention(element);
    const nouveau = `${brouillon.slice(0, menu.debut)}${marqueur} ${brouillon.slice(curseur)}`;
    surBrouillon(nouveau);
    setMenu(null);
    // Le curseur est replacé après la puce, sinon il retombe en fin de texte.
    const position = menu.debut + marqueur.length + 1;
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(position, position); } });
  };

  const signalerSaisie = useCallback(() => {
    const maintenant = Date.now();
    if (maintenant - derniereSaisie.current > 2500) {
      derniereSaisie.current = maintenant;
      surSaisie?.(true);
    }
    clearTimeout(arretSaisie.current);
    arretSaisie.current = setTimeout(() => { derniereSaisie.current = 0; surSaisie?.(false); }, 4000);
  }, [surSaisie]);

  const ajouterFichiers = useCallback(async (liste) => {
    const acceptes = [];
    Array.from(liste || []).forEach((f) => {
      if (!MIMES_ACCEPTES.includes(f.type)) {
        acceptes.push({ cle: `${f.name}-${Date.now()}`, nom: f.name, taille: f.size, erreur: i18n.t('conversations:saisie.formatNonAccepte') });
      } else if (f.size > TAILLE_MAX * 4) {
        acceptes.push({ cle: `${f.name}-${Date.now()}`, nom: f.name, taille: f.size, erreur: i18n.t('conversations:saisie.tropVolumineux') });
      } else {
        acceptes.push({ cle: `${f.name}-${f.size}-${Date.now()}`, nom: f.name, taille: f.size, image: f.type.startsWith('image/'), brut: f, progression: 0 });
      }
    });
    if (!acceptes.length) return;
    setFichiers((prev) => [...prev, ...acceptes]);
    for (const item of acceptes.filter((a) => a.brut)) {
      try {
        const { fichier, largeur, hauteur } = await compresserImage(item.brut);
        if (fichier.size > TAILLE_MAX) throw new Error(i18n.t('conversations:saisie.tropVolumineuxMax'));
        const apercu = item.image ? URL.createObjectURL(fichier) : null;
        setFichiers((prev) => prev.map((f) => (f.cle === item.cle ? { ...f, apercu, taille: fichier.size } : f)));
        const piece = await televerser(fichier, {
          largeur, hauteur,
          onProgress: (p) => setFichiers((prev) => prev.map((f) => (f.cle === item.cle ? { ...f, progression: p } : f))),
        });
        setFichiers((prev) => prev.map((f) => (f.cle === item.cle ? { ...f, piece, progression: 100 } : f)));
      } catch (e) {
        const msg = e?.response?.data?.message || e.message || i18n.t('conversations:saisie.envoiImpossible');
        setFichiers((prev) => prev.map((f) => (f.cle === item.cle ? { ...f, erreur: msg } : f)));
      }
    }
  }, [televerser]);

  useImperativeHandle(ref, () => ({ ajouterFichiers, focus: () => zone.current?.focus() }), [ajouterFichiers]);

  /** Bouton « Citer un dossier » : pose un `#` au curseur et ouvre la recherche. */
  const citerDossier = () => {
    const el = zone.current;
    const curseur = el?.selectionStart ?? brouillon.length;
    const avant = brouillon.slice(0, curseur);
    const jeton = avant.length > 0 && !/\s$/.test(avant) ? ' #' : '#';
    surBrouillon(`${avant}${jeton}${brouillon.slice(curseur)}`);
    const position = curseur + jeton.length;
    setMenu({ type: 'dossier', debut: position - 1, jeton: '' });
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(position, position); } });
  };

  const retirer = (cle) => setFichiers((prev) => {
    const f = prev.find((x) => x.cle === cle);
    if (f?.apercu) URL.revokeObjectURL(f.apercu);
    return prev.filter((x) => x.cle !== cle);
  });

  const enCours = fichiers.some((f) => !f.piece && !f.erreur);
  const pieces = fichiers.filter((f) => f.piece).map((f) => f.piece.pieceTrackingId);
  const peutEnvoyer = !desactive && !enCours && (brouillon.trim().length > 0 || pieces.length > 0);

  const envoyer = () => {
    if (!peutEnvoyer) return;
    surEnvoyer({ contenu: brouillon.trim(), priorite, pieceJointeIds: pieces, replyToId: reponseA?.messageTrackingId || null });
    setPriorite('NORMAL');
    setMenu(null);
    fichiers.forEach((f) => f.apercu && URL.revokeObjectURL(f.apercu));
    setFichiers([]);
    if (astuce) { marquerAstuceEntreeVue(); setAstuce(false); }
    clearTimeout(arretSaisie.current);
    derniereSaisie.current = 0;
    surSaisie?.(false);
  };

  const surTouche = (e) => {
    if (menu) {
      if (e.key === 'Escape') { e.preventDefault(); setMenu(null); return; }
      if (options.length > 0) {
        if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => (i + 1) % options.length); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => (i - 1 + options.length) % options.length); return; }
        // Entrée choisit tant que le menu est ouvert : c'est le geste attendu, et
        // il ne doit surtout pas envoyer un message à moitié adressé.
        if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choisir(options[index]); return; }
      }
      // Recherche de dossier en cours : Entrée attend les résultats plutôt que
      // d'envoyer « #0363 » en texte brut.
      if (menu.type === 'dossier' && dossiers.chargement && e.key === 'Enter') { e.preventDefault(); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); envoyer(); }
    if (e.key === 'Escape' && reponseA) surAnnulerReponse();
  };

  const surColler = (e) => {
    const items = Array.from(e.clipboardData?.items || []).filter((i) => i.kind === 'file');
    if (items.length) {
      e.preventDefault();
      ajouterFichiers(items.map((i) => i.getAsFile()).filter(Boolean));
    }
  };

  return (
    <div style={{ borderTop: `1px solid ${T.bord}`, background: T.blanc, padding: '12px 16px' }}>
      {reponseA && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '4px 8px',
          background: T.fondBulle, borderRadius: T.rayon.champ, fontSize: T.taille.meta,
        }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 600, color: T.vert }}>{t('saisie.reponseA', { nom: reponseA.auteurNom })}</span>
            <span style={{ color: T.muet }}> - {reponseA.supprime ? t('saisie.messageSupprime') : texteSansMarqueurs(reponseA.contenu)}</span>
          </span>
          <button type="button" onClick={surAnnulerReponse} aria-label={t('saisie.annulerReponse')}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.muet, width: 28, height: 28 }}>
            <CloseOutlined />
          </button>
        </div>
      )}

      {fichiers.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {fichiers.map((f) => (
            <div key={f.cle} title={f.erreur || f.nom}
              style={{
                position: 'relative', width: f.image && !f.erreur ? 72 : 200, height: 72, border: `1px solid ${f.erreur ? T.rouge : T.bord}`,
                borderRadius: T.rayon.champ, overflow: 'hidden', background: T.fond, display: 'flex', alignItems: 'center', gap: 8, padding: f.image && !f.erreur ? 0 : '0 8px',
              }}>
              {f.image && f.apercu && !f.erreur ? (
                <img src={f.apercu} alt={f.nom} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <>
                  <FilePdfOutlined style={{ fontSize: 20, color: f.erreur ? T.rouge : '#b91c1c', flexShrink: 0 }} />
                  <span style={{ fontSize: T.taille.mini, minWidth: 0, overflow: 'hidden' }}>
                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nom}</span>
                    <span style={{ color: f.erreur ? T.rouge : T.muet }}>{f.erreur || tailleLisible(f.taille)}</span>
                  </span>
                </>
              )}
              {!f.piece && !f.erreur && (
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
                  <Progress percent={f.progression} size="small" showInfo={false} strokeColor={T.vert} style={{ margin: 0 }} />
                </div>
              )}
              <button type="button" onClick={() => retirer(f.cle)} aria-label={t('saisie.retirer', { nom: f.nom })}
                style={{
                  position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', border: 'none',
                  background: 'rgba(17,24,39,0.7)', color: '#fff', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <CloseOutlined />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <input ref={entree} type="file" multiple accept={MIMES_ACCEPTES.join(',')} style={{ display: 'none' }}
          onChange={(e) => { ajouterFichiers(e.target.files); e.target.value = ''; }} />
        <Tooltip title={t('saisie.joindreAide')}>
          <button type="button" onClick={() => entree.current?.click()} aria-label={t('saisie.joindre')} disabled={desactive}
            style={{
              width: 36, height: 36, border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ, background: T.blanc,
              cursor: 'pointer', color: T.muet, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
            <PaperClipOutlined />
          </button>
        </Tooltip>
        <Tooltip title={t('saisie.citerDossierAide')}>
          <button type="button" aria-label={t('saisie.citerDossier')} disabled={desactive}
            onMouseDown={(e) => e.preventDefault()} onClick={citerDossier}
            style={{
              width: 36, height: 36, border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ, background: T.blanc,
              cursor: 'pointer', color: T.muet, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
            <FolderOpenOutlined />
          </button>
        </Tooltip>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {menu?.type === 'dossier' && (
            <div role="listbox" aria-label={t('saisie.citerDossier')}
              style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0, background: T.blanc,
                border: `1px solid ${T.bord}`, borderRadius: T.rayon.carte, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                maxHeight: 260, overflowY: 'auto', zIndex: 30,
              }}>
              {dossiers.liste.length === 0 && (
                <div style={{ padding: '10px 12px', fontSize: T.taille.meta, color: T.faible }}>
                  {menu.jeton.length < MIN_RECHERCHE_DOSSIER
                    ? t('saisie.tapezDossier')
                    : dossiers.chargement ? t('saisie.recherche') : t('saisie.aucunDossier')}
                </div>
              )}
              {dossiers.liste.map((d, i) => (
                <div key={d.sinistreTrackingId} role="option" aria-selected={i === index}
                  onMouseDown={(e) => { e.preventDefault(); choisir(d); }}
                  onMouseEnter={() => setIndex(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', cursor: 'pointer',
                    background: i === index ? T.vertPale : T.blanc,
                  }}>
                  <FolderOpenOutlined style={{ color: T.vert, fontSize: 16, flexShrink: 0 }} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: T.taille.meta, fontWeight: 600, color: T.texte }}>
                      {d.numeroSinistreLocal}
                      {STATUT_MAP[d.statut] && (
                        <span style={{ fontWeight: 400, color: T.muet }}> · {STATUT_MAP[d.statut].label}</span>
                      )}
                    </span>
                    <span style={{ display: 'block', fontSize: T.taille.mini, color: T.muet, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[d.assureNomComplet, d.assureImmatriculation].filter(Boolean).join(' · ') || '-'}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {menu?.type === 'mention' && (
            <div role="listbox" aria-label={t('saisie.mentionner')}
              style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0, background: T.blanc,
                border: `1px solid ${T.bord}`, borderRadius: T.rayon.carte, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                maxHeight: 260, overflowY: 'auto', zIndex: 30,
              }}>
              {propositions.length === 0 && (
                <div style={{ padding: '10px 12px', fontSize: T.taille.meta, color: T.faible }}>
                  {membres.filter((m) => m.actif !== false && m.utilisateurTrackingId !== moiTrackingId).length === 0
                    ? t('saisie.personneDAutre')
                    : t('saisie.aucunMembre')}
                </div>
              )}
              {propositions.map((m, i) => (
                <div key={m.utilisateurTrackingId} role="option" aria-selected={i === index}
                  onMouseDown={(e) => { e.preventDefault(); choisir(m); }}
                  onMouseEnter={() => setIndex(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', cursor: 'pointer',
                    background: i === index ? T.vertPale : T.blanc,
                  }}>
                  <Avatar nom={m.nomComplet} initiales={m.initiales} taille={26} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: T.taille.meta, fontWeight: 600, color: T.texte, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.nomComplet}
                    </span>
                    {m.profil && <span style={{ display: 'block', fontSize: T.taille.mini, color: T.muet }}>{m.profil}</span>}
                  </span>
                  {m.enLigne && <span aria-label={t('presence.enLigne')} style={{ width: 8, height: 8, borderRadius: '50%', background: T.vert, flexShrink: 0 }} />}
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={zone}
            value={brouillon}
            disabled={desactive}
            placeholder={t('saisie.placeholder')}
            aria-label={t('saisie.message')}
            rows={1}
            onChange={(e) => { surBrouillon(e.target.value); signalerSaisie(); analyser(e.target.value, e.target.selectionStart ?? e.target.value.length); }}
            onKeyDown={surTouche}
            onClick={(e) => analyser(brouillon, e.target.selectionStart ?? 0)}
            onPaste={surColler}
            style={{
              width: '100%', minHeight: 36, font: 'inherit', fontSize: T.taille.corps, lineHeight: 1.5, padding: '7px 12px',
              border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ, resize: 'none', outline: 'none', background: T.blanc, color: T.texte,
              display: 'block', boxSizing: 'border-box',
            }}
            onFocus={(e) => { e.target.style.borderColor = T.vert; }}
            onBlur={(e) => { e.target.style.borderColor = T.bord; setMenu(null); }}
          />
        </div>
        {/* Le controle n'affichait que « Normal », sans dire de quoi. Un
            aria-label suffit au lecteur d'ecran, pas a l'oeil : le prefixe rend
            l'intitule visible. La liste deroulante, elle, reste nue - repeter
            « Priorite » sur chacune des trois options n'apprendrait rien. */}
        <Select
          value={priorite} onChange={setPriorite} size="middle" aria-label={t('saisie.prioriteMessage')} disabled={desactive}
          prefix={<span style={{ color: T.muet, fontSize: T.taille.meta }}>{t('saisie.priorite')}</span>}
          options={PRIORITES.map((p) => ({ value: p.code, label: p.libelle }))}
          style={{ width: 176, flexShrink: 0 }}
        />
        <Tooltip title={t('saisie.envoyerAide')}>
          <button type="button" onClick={envoyer} disabled={!peutEnvoyer} aria-label={t('saisie.envoyer')}
            style={{
              width: 36, height: 36, border: 'none', borderRadius: T.rayon.champ, flexShrink: 0,
              background: peutEnvoyer ? T.vert : T.bord, color: '#fff', cursor: peutEnvoyer ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <SendOutlined />
          </button>
        </Tooltip>
      </div>
      {astuce && (
        <div style={{ fontSize: T.taille.mini, color: T.faible, marginTop: 4 }}>
          {t('saisie.astuce')}
        </div>
      )}
    </div>
  );
});

export default ZoneSaisie;
